import React, { useMemo, useState } from 'react';
import { Box, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { BucketEmailStatus, CollectionClient } from '../model';
import { formatDollar } from '../utils/format';
import { TONE_COLOR_BY_KEY, ToneKey } from '../../shared/toneColors';
import BucketEmailStatusIcon from './BucketEmailStatusIcon';

export type AgingBucket = 'Past Due' | 'Friendly' | 'Second Notice' | 'Urgent';

const BUCKETS: AgingBucket[] = ['Past Due', 'Friendly', 'Second Notice', 'Urgent'];

const LABEL_TO_TONE_KEY: Record<AgingBucket, ToneKey> = {
  'Past Due': 'past_due',
  Friendly: 'friendly',
  'Second Notice': 'second_notice',
  Urgent: 'urgent',
};

const dayRangeLabel = (bucket: AgingBucket, pastDue: number, friendly: number, secondNotice: number): string => {
  switch (bucket) {
    case 'Past Due':
      return `1–${pastDue} days`;
    case 'Friendly':
      return `${pastDue + 1}–${friendly} days`;
    case 'Second Notice':
      return `${friendly + 1}–${secondNotice} days`;
    case 'Urgent':
      return `${secondNotice + 1}+ days`;
    default:
      return '';
  }
};

interface BucketAggregate {
  total: number;
  count: number;
}

const aggregateBuckets = (clients: CollectionClient[]): Record<AgingBucket, BucketAggregate> => {
  const result: Record<AgingBucket, BucketAggregate> = {
    'Past Due': { total: 0, count: 0 },
    Friendly: { total: 0, count: 0 },
    'Second Notice': { total: 0, count: 0 },
    Urgent: { total: 0, count: 0 },
  };
  clients.forEach((client) => {
    if (BUCKETS.includes(client.tone as AgingBucket)) {
      result[client.tone as AgingBucket].total += client.total_past_due;
      result[client.tone].count += 1;
    }
  });
  return result;
};

interface CollectionClientsAgingSummaryProps {
  clients: CollectionClient[];
  pastDueThresholdDays: number;
  friendlyThresholdDays: number;
  secondNoticeThresholdDays: number;
  selectedBucket: AgingBucket | null;
  onSelectBucket: (bucket: AgingBucket | null) => void;
  loading: boolean;
  // Omitted while config loads — icons are hidden rather than guessed.
  emailStatus?: Record<ToneKey, BucketEmailStatus>;
}

const TILE_HEIGHT = 128;

const CollectionClientsAgingSummary: React.FC<CollectionClientsAgingSummaryProps> = ({
  clients,
  pastDueThresholdDays,
  friendlyThresholdDays,
  secondNoticeThresholdDays,
  selectedBucket,
  onSelectBucket,
  loading,
  emailStatus,
}) => {
  const aggregates = useMemo(() => aggregateBuckets(clients), [clients]);
  // One shared slot: hovering an icon or focusing a card opens that bucket's tooltip.
  const [tooltipBucket, setTooltipBucket] = useState<AgingBucket | null>(null);

  return (
    <Grid container spacing={2}>
      {BUCKETS.map((bucket) => {
        const color = TONE_COLOR_BY_KEY[LABEL_TO_TONE_KEY[bucket]];
        const isSelected = selectedBucket === bucket;
        const handleClick = () => onSelectBucket(isSelected ? null : bucket);
        const agg = aggregates[bucket];
        const range = dayRangeLabel(bucket, pastDueThresholdDays, friendlyThresholdDays, secondNoticeThresholdDays);
        const countLabel = agg.count === 1 ? '1 client' : `${agg.count} clients`;
        const status = emailStatus?.[LABEL_TO_TONE_KEY[bucket]];
        const statusLabel = status ? `, automated emails ${status.enabled ? 'on' : 'off'}: ${status.reason}` : '';

        return (
          <Grid item xs={12} sm={3} key={bucket}>
            <Paper
              elevation={isSelected ? 2 : 1}
              onClick={handleClick}
              onFocus={() => setTooltipBucket(bucket)}
              onBlur={() => setTooltipBucket(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={`${bucket} aging bucket, ${range}, ${loading ? 'loading' : `${formatDollar(agg.total)}, ${countLabel}`}${statusLabel}`}
              sx={{
                p: 2,
                height: TILE_HEIGHT,
                cursor: 'pointer',
                border: isSelected ? `2px solid ${color}` : '1px solid',
                borderColor: isSelected ? color : 'divider',
                bgcolor: isSelected ? `${color}10` : 'background.paper',
                transition: 'box-shadow 150ms ease, background-color 150ms ease',
                outline: 'none',
                '&:hover': {
                  boxShadow: 3,
                  bgcolor: `${color}10`,
                },
                '&:focus-visible': {
                  boxShadow: 4,
                  borderColor: color,
                },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="h6" sx={{ color, lineHeight: 1.2 }}>
                  {bucket}
                </Typography>
                {status && (
                  <BucketEmailStatusIcon
                    status={status}
                    tooltipOpen={tooltipBucket === bucket}
                    onTooltipOpen={() => setTooltipBucket(bucket)}
                    onTooltipClose={() => setTooltipBucket(null)}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {range}
              </Typography>
              {loading ? (
                <>
                  <Skeleton sx={{ mt: 1 }} width={140} height={32} />
                  <Skeleton width={80} height={16} />
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ mt: 1, color, lineHeight: 1.2 }}>
                    {formatDollar(agg.total)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {countLabel}
                  </Typography>
                </>
              )}
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CollectionClientsAgingSummary;
